import React, { useEffect, useRef } from 'react';
import { trackScrollDepth, trackTimeOnPage, trackPageView, trackVisit, trackClick } from '../utils/analytics';

const AnalyticsTracker = ({ pageName, customData = {} }) => {
  const startTimeRef = useRef(Date.now());
  const maxScrollRef = useRef(0);
  const scrollThresholdsRef = useRef(new Set());
  
  useEffect(() => {
    // Track page view
    trackPageView(pageName, customData);
    // First-party visit tracking for the admin analytics panel
    trackVisit(pageName);
    
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Calculate scroll percentage
      const scrollPercent = Math.round(
        (scrollTop / (documentHeight - windowHeight)) * 100
      );
      
      // Update max scroll depth
      if (scrollPercent > maxScrollRef.current) {
        maxScrollRef.current = scrollPercent;
      }
      
      // Track scroll depth milestones (25%, 50%, 75%, 90%, 100%)
      const thresholds = [25, 50, 75, 90, 100];
      thresholds.forEach(threshold => {
        if (scrollPercent >= threshold && !scrollThresholdsRef.current.has(threshold)) {
          scrollThresholdsRef.current.add(threshold);
          trackScrollDepth(threshold);
        }
      });
    };
    
    const handleBeforeUnload = () => {
      // Track time on page when user leaves
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      trackTimeOnPage(timeSpent);
    };
    
    // Add event listeners
    const handleClick = (e) => trackClick(e);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('click', handleClick, true);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Track time on page periodically for long sessions
    const timeTrackingInterval = setInterval(() => {
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      // Track every 30 seconds for sessions longer than 30 seconds
      if (timeSpent > 0 && timeSpent % 30 === 0) {
        trackTimeOnPage(timeSpent);
      }
    }, 30000);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick, true);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(timeTrackingInterval);
      
      // Track final time on page and max scroll depth
      const finalTimeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (finalTimeSpent > 5) { // Only track if user spent more than 5 seconds
        trackTimeOnPage(finalTimeSpent);
      }
      if (maxScrollRef.current > 0) {
        trackScrollDepth(maxScrollRef.current);
      }
    };
  }, [pageName, customData]);
  
  return null; // This component doesn't render anything
};

export default AnalyticsTracker;